import { Component, computed, inject, Renderer2, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterLink, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AppTopbar } from './app.topbar';
import { AppSidebar } from './app.sidebar';
import { LayoutService } from '../service/layout.service';
import { TranslationService } from '../service/translation.service';
import { ToastModule } from 'primeng/toast';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { TenantSwitcherComponent } from '@/cloud/components/tenant-switcher/tenant-switcher.component';
import { AssistantDrawerComponent } from '@/cloud/components/assistant/assistant-drawer.component';
import { AssistantService } from '@/cloud/services/assistant.service';

@Component({
    selector: 'app-layout',
    standalone: true,
    imports: [CommonModule, AppTopbar, AppSidebar, RouterModule, ToastModule, TenantSwitcherComponent, AssistantDrawerComponent],
    template: `<div class="layout-wrapper" [ngClass]="containerClass">
        <app-topbar [isCloudInstanceInput]="true" [systemNameInput]="" [loggedInUserInput]="user" [userEmailForGravatarInput]="email">
            @if (!hideTenantSwitcher()) {
                <app-tenant-switcher tenant-switcher />
            }
        </app-topbar>
        <!-- Pass the computed signal value (array) to child instead of the signal itself -->
        <app-sidebar [menuItems]="menuItems()"></app-sidebar>
        <div class="layout-main-container">
            <div class="layout-main">
                <p-toast position="bottom-right" />
                <router-outlet></router-outlet>
            </div>
            <!-- <app-footer></app-footer> -->
        </div>
        <div class="layout-mask animate-fadein"></div>
        <app-assistant-drawer />
    </div> `
})
export class AppLayout {
    private api = inject(CloudApiService);
    private translationService = inject(TranslationService);
    private assistant = inject(AssistantService);
    /** Pages that span every tenant (route data `hideTenantSwitcher`) have no tenant to switch. */
    readonly hideTenantSwitcher = signal(false);
    user = computed(() => this.api.currentUser()?.username || undefined);
    email = computed(() => this.api.currentUser()?.email || undefined);
    overlayMenuOpenSubscription: Subscription;

    menuOutsideClickListener: any;

    @ViewChild(AppSidebar) appSidebar!: AppSidebar;

    @ViewChild(AppTopbar) appTopBar!: AppTopbar;

    /** The Issues section is shown when the user can view issues in any of their namespaces (or is a superadmin). */
    readonly showIssues = computed(() => {
        const user = this.api.currentUser();
        if (!user) return false;
        if (user.superAdmin) return true;
        return (user.permissions || []).some((p) => this.api.hasCapability(p.appNamespaceId, 'issues.view'));
    });

    // Build menu dynamically based on current user (superAdmin OR namespace admin)
    menuItems = computed(() => {
        const t = this.translationService.t();
        const user = this.api.currentUser();
        const main = {
            label: t('menu.mainMenu'),
            items: [
                { label: t('menu.dashboard'), icon: 'pi pi-fw pi-home', routerLink: ['/dashboard'], routerLinkActiveOptions: { paths: 'subset', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' } },
                ...(this.assistant.serviceDeskAvailable() ? [{ label: t('menu.serviceDeskChat'), icon: 'pi pi-fw pi-sparkles', routerLink: ['/service-desk'] }] : []),
                { label: t('files.title'), icon: 'pi pi-fw pi-folder-open', routerLink: ['/files'], routerLinkActiveOptions: { paths: 'subset', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' } },
                { label: t('menu.packages'), icon: 'pi pi-fw pi-box', routerLink: ['/packages'] },
                ...(this.showIssues() ? [{ label: t('menu.issues'), icon: 'pi pi-fw pi-github', routerLink: ['/issues'], routerLinkActiveOptions: { paths: 'subset', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' } }] : []),
                { label: t('menu.firmware') || 'Firmware', icon: 'pi pi-fw pi-microchip', routerLink: ['/firmware'] }
            ]
        } as any;

        // Admin section is shown to superadmins and anyone whose role can manage
        // members or namespace settings somewhere (covers custom roles).
        const isSuperAdmin = !!user?.superAdmin;
        const showAdmin = isSuperAdmin || this.api.hasAnyAdminCapability();

        const adminItems = [
            { label: t('menu.users'), icon: 'fa fa-users', routerLink: ['/admin/users'] },
            { label: t('menu.tenants'), icon: 'fa fa-building', routerLink: ['/admin/tenants'] }
        ];

        // Role definitions are editable by super admins only
        if (isSuperAdmin) {
            adminItems.push({ label: t('menu.roles'), icon: 'fa fa-user-shield', routerLink: ['/admin/roles'] });
            adminItems.push({ label: t('menu.platformKeys'), icon: 'fa fa-key', routerLink: ['/admin/platform-keys'] });
            adminItems.push({ label: t('menu.serviceDesk'), icon: 'fa fa-headset', routerLink: ['/admin/service-desk'] });
        }

        const admin = {
            label: t('menu.admin'),
            items: adminItems
        } as any;

        return showAdmin ? [main, admin] : [main];
    });

    constructor(
        public layoutService: LayoutService,
        public renderer: Renderer2,
        public router: Router
    ) {
        this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
            if (!this.menuOutsideClickListener) {
                this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
                    if (this.isOutsideClicked(event)) {
                        this.hideMenu();
                    }
                });
            }

            if (this.layoutService.layoutState().staticMenuMobileActive) {
                this.blockBodyScroll();
            }
        });

        this.hideTenantSwitcher.set(AppLayout.routeHidesTenantSwitcher(this.router.routerState.snapshot.root));
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.hideMenu();
            this.hideTenantSwitcher.set(AppLayout.routeHidesTenantSwitcher(this.router.routerState.snapshot.root));
        });
    }

    private static routeHidesTenantSwitcher(route: ActivatedRouteSnapshot): boolean {
        for (let r: ActivatedRouteSnapshot | null = route; r; r = r.firstChild) {
            if (r.data?.['hideTenantSwitcher']) return true;
        }
        return false;
    }

    isOutsideClicked(event: MouseEvent) {
        const sidebarEl = document.querySelector('.layout-sidebar');
        const topbarEl = document.querySelector('.layout-menu-button');
        const eventTarget = event.target as Node;

        return !(sidebarEl?.isSameNode(eventTarget) || sidebarEl?.contains(eventTarget) || topbarEl?.isSameNode(eventTarget) || topbarEl?.contains(eventTarget));
    }

    hideMenu() {
        this.layoutService.layoutState.update((prev) => ({ ...prev, overlayMenuActive: false, staticMenuMobileActive: false, menuHoverActive: false }));
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }
        this.unblockBodyScroll();
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        } else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

    get containerClass() {
        return {
            'layout-overlay': this.layoutService.layoutConfig().menuMode === 'overlay',
            'layout-static': this.layoutService.layoutConfig().menuMode === 'static',
            'layout-static-inactive': this.layoutService.layoutState().staticMenuDesktopInactive && this.layoutService.layoutConfig().menuMode === 'static',
            'layout-overlay-active': this.layoutService.layoutState().overlayMenuActive,
            'layout-mobile-active': this.layoutService.layoutState().staticMenuMobileActive,
            'layout-assistant-docked': this.assistant.docked()
        };
    }

    ngOnDestroy() {
        if (this.overlayMenuOpenSubscription) {
            this.overlayMenuOpenSubscription.unsubscribe();
        }

        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }
    }
}
